import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Button, getColor, Icons, Tabs, Tooltip, Typography } from "fds";
import "../index.scss";
import utilsCommon from "../../../shared/utils/common";
import { Workgroup } from "../../../types/workgroup";
import ClientFile from "./ClientFile";
import ClientMeeting from "./ClientMeeting";
import useWorkgroupStore from "../../../redux/dispatcher/useWorkgroupStore";
import { useParams } from "react-router-dom";
import { handleSetFavorite } from "../../../shared/utils/api";
import { t } from "i18next";
import dayjs from "dayjs";
import ClientHistory from "./ClientHistory";
import WorkgroupAvatar from "../../../components/WorkgroupAvatar";

const ClientWorkgorup = () => {
  const { workgroupId } = useParams<{ workgroupId: string }>();
  const { workgroupState, updateWorkgroupFavorite } = useWorkgroupStore();
  const [workgroupList, changeWorkgroupList] = useState<Workgroup[]>();
  const [tab, changeTab] = useState(0);
  const [focusedWorkgroupInfo, changeFocusedWorkgroupInfo] = useState<
    Workgroup | undefined
  >(undefined);
  const [refreshCnt, changeRefreshCnt] = useState(0);

  const TAB_VALUES = [
    {
      key: "file",
      resource: t("common.file"),
      index: 0,
      hidden:
        workgroupList?.filter(
          (item) => item.workgroupId === focusedWorkgroupInfo?.workgroupId
        )[0]?.workgroupType === "HUB",
    },
    {
      key: "meeting",
      resource: t("common.meetings"),
      index: 1,
      hidden:
        utilsCommon.checkMenuAvailable("zoom") === false ||
        workgroupList?.filter(
          (item) => item.workgroupId === focusedWorkgroupInfo?.workgroupId
        )[0]?.workgroupType === "HUB",
    },
    { key: "log", resource: t("common.log"), index: 2, hidden: false },
  ];
  useEffect(() => {
    changeFocusedWorkgroupInfo(
      workgroupList?.filter((item) => item.workgroupId === workgroupId)[0]
    );
    changeTab(
      workgroupList?.filter((item) => item.workgroupId === workgroupId)[0]
        ?.workgroupType === "HUB"
        ? 2
        : 0
    );
  }, [workgroupId, workgroupList]);
  useEffect(() => {
    let sorted_workgroup = workgroupState.workgroupSidemenuList
      ?.filter(
        (item: Workgroup) =>
          item.endDate === -1 || item.endDate > dayjs().valueOf()
      )
      .slice()
      .sort((a: Workgroup, b: Workgroup) => {
        if (a.favorite === "Y" && b.favorite !== "Y") return -1;
        if (a.favorite !== "Y" && b.favorite === "Y") return 1;
        return 0; // 원래 순서 유지
      });
    changeWorkgroupList(sorted_workgroup);
  }, [workgroupState.workgroupSidemenuList]);
  /** 워크그룹 웹으로 이동 */
  const handleMoveToWorkgroup = (workgroupId?: string) => {
    if (!workgroupId) return;
    if (utilsCommon.isClientLogin("MAC")) {
      (window as any)?.webkit?.messageHandlers.goWebWorkgroup.postMessage(
        workgroupId
      );
    } else if (utilsCommon.isClientLogin("WINDOWS")) {
      window?.chrome?.webview?.hostObjects.client.goWebWorkgroup(workgroupId);
    }
  };

  const renderClientHeader = (workgroup?: Workgroup) => {
    return (
      <Box height={88} classes="clientEco-header" alignItems="center">
        <WorkgroupAvatar item={workgroup} size="48" />
        <Box
          direction="column"
          width={"calc(100% - 95px)"}
          classes="mg ml-12"
          justifyContent="center"
        >
          <Box height={32} alignItems="center">
            {workgroup?.workgroupType &&
              ["HUB", "PRIVATE"].includes(workgroup?.workgroupType) && (
                <Icons variant="fill" label="lock" classes="mg mr-4" />
              )}
            <Tooltip
              message={workgroup?.workgroupName}
              maxWidth={"calc(100% - 32*3px)"}
            >
              <Typography type="subtitle1" ellipsis>
                {workgroup?.workgroupName}
              </Typography>
            </Tooltip>
            <Tooltip
              message={
                workgroup?.favorite === "Y"
                  ? t("workgroup.unfavWorkgroup")
                  : t("workgroup.favWorkgroup")
              }
            >
              <Button
                btnStyle="icon"
                onClick={() =>
                  handleSetFavorite(
                    workgroup?.workgroupId,
                    updateWorkgroupFavorite,
                    workgroupList
                  )
                }
                leadingIcon={
                  workgroup?.favorite === "N" ? (
                    <Icons variant="line" label="star" isOn />
                  ) : (
                    <Icons variant="fill" label="star" isColored />
                  )
                }
              />
            </Tooltip>
            <Tooltip message={t("common.moveToWorkgroup")}>
              <Button
                btnStyle="icon"
                onClick={() => handleMoveToWorkgroup(workgroup?.workgroupId)}
                leadingIcon={<Icons variant="line" label="export" />}
              />
            </Tooltip>
          </Box>
          <Tooltip message={workgroup?.desc}>
            <Typography
              type="caption1_rg"
              ellipsis
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {workgroup?.desc}
            </Typography>
          </Tooltip>
        </Box>
      </Box>
    );
  };
  return (
    <Box direction="column" width={"calc(100% - 64px)"}>
      {renderClientHeader(
        workgroupList?.filter(
          (item) => item.workgroupId === focusedWorkgroupInfo?.workgroupId
        )[0]
      )}
      <Box classes="clientEco-tabs">
        <Tabs
          type={"leftFull"}
          selected={tab}
          textAlign={"left"}
          selectTab={(newTab) => changeTab(newTab)}
          tabs={TAB_VALUES.map((item) => {
            return {
              text: item.resource,
              color: "default",
              isHidden: item.hidden,
            };
          })}
          paddingLeft={20}
          size={"lg"}
        />
        <Tooltip message={t("common.refresh")} classes="mg ml-auto mr-16">
          <Button
            btnStyle="icon"
            classes="mg ml-auto"
            onClick={() => window.location.reload()}
            leadingIcon={<Icons variant="line" label="refresh" />}
          />
        </Tooltip>
      </Box>
      {!focusedWorkgroupInfo?.workgroupId ? (
        <></>
      ) : tab === 0 ? (
        <ClientFile
          workgroupId={focusedWorkgroupInfo?.workgroupId}
          refreshCnt={refreshCnt}
        />
      ) : tab === 1 ? (
        <ClientMeeting
          workgroupId={focusedWorkgroupInfo?.workgroupId}
          refreshCnt={refreshCnt}
        />
      ) : (
        <ClientHistory
          focusedWorkgroupInfo={focusedWorkgroupInfo}
          refreshCnt={refreshCnt}
        />
      )}
    </Box>
  );
};

export default ClientWorkgorup;
